// Dexie-backed adapters for the platform-agnostic chat sync hooks +
// message persistence helpers that live in `@ping/ui` (under
// `packages/ui/src/sync/`). The shared layer reads/writes through
// `MessagesPersister` and `ConversationsPersister` interfaces;
// this file wires those interfaces to the apps/web Dexie singleton
// (`apps/web/src/core/persistence/db.ts`).
//
// The KVStore adapter is shared with the MLS-conversation
// orchestration in `./mlsConversation.ts` — exported there as
// `kvAdapter` and re-exported here for callers wiring up the
// incoming-conversations hook (which writes the same
// `mls.conv.<id>` / `mls.invited.<id>` markers that mlsConversation
// produces on the inviter side).

import type { ConversationsPersister, MessagesPersister } from '@ping/ui';

import { db } from '@core/persistence/db';

import { isLocallyRemovedMessage } from './locallyRemovedMessages';
import { kvAdapter } from './mlsConversation';

export { kvAdapter };

// Dexie's `messages` table maps 1:1 to the shared `MessageRecord`
// shape — the only translation needed is for `epoch` / `serverSeq`,
// which Dexie requires as plain `number` while the shared shape
// allows them to be optional (different host stores treat them
// differently). All current callers in the hooks supply both
// fields, but we fall back to `0` / `Date.now()` defensively so a
// future caller that omits one doesn't trip Dexie's stricter
// schema.
export const webMessagesPersister: MessagesPersister = {
  async getByContentHash(hash) {
    const row = await db.messages.where('contentHash').equals(hash).first();
    return row ?? undefined;
  },
  async put(record) {
    // Delete-for-me durability: a message the local user removed only on this
    // device must never be resurrected by a resume/replay re-insert.
    if (isLocallyRemovedMessage(record.contentHash)) return;
    await db.messages.put({
      ...record,
      epoch: record.epoch ?? 0,
      serverSeq: record.serverSeq ?? Date.now(),
    });
  },
  async delete(id) {
    await db.messages.delete(id);
  },
  async update(id, patch) {
    // Dexie's `update` ignores keys whose values are `undefined`, so
    // the partial-record shape from `useIncomingMessages` (which only
    // stamps the fields it knows about) round-trips cleanly.
    await db.messages.update(id, patch);
  },
};

// Dexie's `conversations` table is a superset of the shared
// `ConversationRecord` shape — `archivedAt` lives on Dexie but
// isn't surfaced through the adapter (the chat sync hooks never
// archive). All fields the adapter writes/reads round-trip cleanly.
export const webConversationsPersister: ConversationsPersister = {
  async get(id) {
    const row = await db.conversations.get(id);
    if (!row) return undefined;
    return {
      id: row.id,
      kind: row.kind,
      // Dexie allows `name` to be undefined; the shared shape
      // requires a string. Use the empty string as a sentinel —
      // the hooks only read `name` from the shape they wrote
      // themselves (via `put`), so an undefined Dexie value
      // wouldn't be observed in practice.
      name: row.name ?? '',
      ...(row.peerAccountId !== undefined ? { peerAccountId: row.peerAccountId } : {}),
      createdAt: row.createdAt,
      ...(row.lastMessageAt !== undefined ? { lastMessageAt: row.lastMessageAt } : {}),
      ...(row.lastMessagePreview !== undefined
        ? { lastMessagePreview: row.lastMessagePreview }
        : {}),
      ...(row.lastMessageSenderAccountId !== undefined
        ? { lastMessageSenderAccountId: row.lastMessageSenderAccountId }
        : {}),
      ...((row as { unreadCount?: number }).unreadCount !== undefined
        ? { unreadCount: (row as { unreadCount?: number }).unreadCount }
        : {}),
      ...(row.joinedViaInvite !== undefined ? { joinedViaInvite: row.joinedViaInvite } : {}),
      ...(row.explicitGroup !== undefined ? { explicitGroup: row.explicitGroup } : {}),
      ...(row.memberAccountIds !== undefined ? { memberAccountIds: row.memberAccountIds } : {}),
      ...(row.memberDisplayNames !== undefined
        ? { memberDisplayNames: row.memberDisplayNames }
        : {}),
      ...(row.memberDeviceNames !== undefined
        ? { memberDeviceNames: row.memberDeviceNames }
        : {}),
      ...(row.memberDeviceAccounts !== undefined
        ? { memberDeviceAccounts: row.memberDeviceAccounts }
        : {}),
      ...(row.memberAvatarUris !== undefined ? { memberAvatarUris: row.memberAvatarUris } : {}),
      // Group/conversation avatar: the resolved (short-lived) URL plus the stable
      // media id it was resolved from. Both must round-trip so the incoming-
      // conversation backfill can tell whether the row already carries an avatar
      // (and re-resolve a fresh URL from the id when it has expired).
      ...(row.avatarUri !== undefined ? { avatarUri: row.avatarUri } : {}),
      ...(row.avatarMediaId !== undefined ? { avatarMediaId: row.avatarMediaId } : {}),
      // C3: carry the owner + roles so the receive-side role-authority check and
      // the Chat Info gating can read them off the conversation row.
      ...(row.ownerAccountId !== undefined ? { ownerAccountId: row.ownerAccountId } : {}),
      ...(row.memberRoles !== undefined ? { memberRoles: row.memberRoles } : {}),
      // Hide/lifecycle markers — the receive path (persistIfNew) reads these to
      // decide whether to un-hide, and they MUST NOT be lost on a re-persist.
      ...(row.deletedAt !== undefined ? { deletedAt: row.deletedAt } : {}),
      ...(row.orphanedAt !== undefined ? { orphanedAt: row.orphanedAt } : {}),
      ...(row.leftAt !== undefined ? { leftAt: row.leftAt } : {}),
      // Leave epoch — MUST round-trip so the re-invite detector (persistIfNew) can
      // compare an incoming live update's epoch against the epoch we left at.
      ...(row.leftAtEpoch !== undefined ? { leftAtEpoch: row.leftAtEpoch } : {}),
      // Departed-member set — MUST round-trip. The receive-side kick/leave handler
      // reads `conv.departedAccountIds` to ACCUMULATE removals (`[...prev, target]`)
      // and the invite/group_name handlers read it to CLEAR re-added members. When
      // this getter omitted it, every kick on a recipient read `undefined` and
      // clobbered the set to just the latest target — so an earlier-removed member
      // lost departed-protection and was re-added by the next authoritative
      // server-roster reconcile (the "removed member reappears" bug).
      ...(row.departedAccountIds !== undefined
        ? { departedAccountIds: row.departedAccountIds }
        : {}),
    };
  },
  async put(record) {
    // PRESERVE-MERGE, not full-replace. A blind `db.conversations.put` drops
    // every field the shared `ConversationRecord` doesn't carry — most
    // dangerously the hide markers `deletedAt` / `orphanedAt` / `leftAt` and the
    // personal-state flags `pinnedAt` / `archivedAt`. That silently resurrected
    // deleted / server-orphaned / left / un-pinned / un-archived rows on every
    // Welcome re-emit (the flapping "New chat" ghost). Spread the existing row
    // first so those markers survive; the record fields below still override
    // identity (name, roster, avatar, …).
    const prev = await db.conversations.get(record.id);
    await db.conversations.put({
      ...(prev ?? {}),
      id: record.id,
      kind: record.kind,
      name: record.name,
      ...(record.peerAccountId !== undefined ? { peerAccountId: record.peerAccountId } : {}),
      createdAt: record.createdAt,
      ...(record.lastMessageAt !== undefined ? { lastMessageAt: record.lastMessageAt } : {}),
      ...(record.lastMessagePreview !== undefined
        ? { lastMessagePreview: record.lastMessagePreview }
        : {}),
      ...(record.lastMessageSenderAccountId !== undefined
        ? { lastMessageSenderAccountId: record.lastMessageSenderAccountId }
        : {}),
      ...(record.joinedViaInvite !== undefined ? { joinedViaInvite: record.joinedViaInvite } : {}),
      // DURABLE group marker must survive a re-persist (never downgrade a group
      // to a mergeable 1:1). Preserve-merge above keeps a prior `true`, and this
      // carries an explicit write through.
      ...(record.explicitGroup !== undefined ? { explicitGroup: record.explicitGroup } : {}),
      ...(record.memberAccountIds !== undefined
        ? { memberAccountIds: record.memberAccountIds }
        : {}),
      ...(record.memberDisplayNames !== undefined
        ? { memberDisplayNames: record.memberDisplayNames }
        : {}),
      ...(record.memberDeviceNames !== undefined
        ? { memberDeviceNames: record.memberDeviceNames }
        : {}),
      ...(record.memberDeviceAccounts !== undefined
        ? { memberDeviceAccounts: record.memberDeviceAccounts }
        : {}),
      ...(record.memberAvatarUris !== undefined
        ? { memberAvatarUris: record.memberAvatarUris }
        : {}),
      ...(record.avatarUri !== undefined ? { avatarUri: record.avatarUri } : {}),
      ...(record.avatarMediaId !== undefined ? { avatarMediaId: record.avatarMediaId } : {}),
      ...(record.ownerAccountId !== undefined ? { ownerAccountId: record.ownerAccountId } : {}),
      ...(record.memberRoles !== undefined ? { memberRoles: record.memberRoles } : {}),
      // Preserve-merge (`...prev`) already keeps a prior `departedAccountIds`, but
      // carry an explicit record value through so a full put can't silently drop it.
      ...(record.departedAccountIds !== undefined
        ? { departedAccountIds: record.departedAccountIds }
        : {}),
      ...(record.leftAtEpoch !== undefined ? { leftAtEpoch: record.leftAtEpoch } : {}),
    });
  },
  async update(id, patch) {
    await db.conversations.update(id, patch);
  },
};
